import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { jobService, adminApi, orderService } from '../../services/api';

export default function PartnerAdminCalendar() {
    const [jobs, setJobs] = useState<any[]>([]);
    const [orders, setOrders] = useState<any[]>([]);
    const [partners, setPartners] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [selectedJob, setSelectedJob] = useState<any | null>(null);
    const [dispatchPartnerId, setDispatchPartnerId] = useState('');
    const [isDispatching, setIsDispatching] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [jobsRes, ordersRes, partnersRes] = await Promise.all([
                jobService.getAdminCalendar(),
                orderService.getAllOrdersAdmin(),  // Use admin endpoint to get all orders
                adminApi.get('api/super-admin/partners') // Just to list available partners to dispatch to
            ]);
            setJobs(jobsRes.data.jobs || []);
            setOrders(ordersRes.data.data || []);  // Admin endpoint returns all orders
            // Filter only verified partners for dispatching
            setPartners(partnersRes.data.data?.filter((p: any) => p.verificationStatus === 'verified') || []);
        } catch (err) {
            console.error('Failed to load admin data', err);
            // Fallback to tenant-scoped orders if admin endpoint fails
            try {
                const fallbackOrders = await orderService.listOrders();
                setOrders(fallbackOrders.data.data || []);
            } catch (fallbackErr) {
                console.error('Failed to load orders (fallback):', fallbackErr);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDispatch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedJob || !dispatchPartnerId) return;
        setIsDispatching(true);
        try {
            await jobService.dispatchJob(selectedJob.id, dispatchPartnerId);
            alert('Job dispatched successfully');
            setSelectedJob(null);
            fetchData();
        } catch (err) {
            alert('Failed to dispatch job');
        } finally {
            setIsDispatching(false);
        }
    };

    const handleCancel = async (jobId: string) => {
        if (!window.confirm('Are you sure you want to cancel this job?')) return;
        try {
            await jobService.cancelJob(jobId);
            fetchData();
        } catch (err) {
            alert('Failed to cancel job');
        }
    };

    const getJobsForDate = (date: Date) => {
        const jobsOnDate = jobs.filter(job => {
            const d = new Date(job.requestedDate || job.createdAt);
            return d.getDate() === date.getDate() &&
                d.getMonth() === date.getMonth() &&
                d.getFullYear() === date.getFullYear();
        });

        const ordersOnDate = orders.filter(order => {
            // Check if order falls within the requested date range
            if (order.dateRange?.startDate) {
                const startDate = new Date(order.dateRange.startDate);
                return startDate.getDate() === date.getDate() &&
                    startDate.getMonth() === date.getMonth() &&
                    startDate.getFullYear() === date.getFullYear();
            }
            return false;
        });

        // Convert orders to job-like format for display
        const formattedOrders = ordersOnDate.map((order: any) => ({
            id: order.id,
            status: order.status || 'pending',
            serviceType: `${order.certificateType} Certificate`,
            propertyAddress: order.propertyAddress || 'Address not specified',
            tenantName: order.tenantName || 'N/A',
            tenantPhone: order.tenantPhone || 'N/A',
            assignedPartnerName: order.partnerName,
            isOrder: true,
            requestedDate: order.dateRange?.startDate,
            createdAt: order.createdAt,
            accessDetails: order.accessDetails
        }));

        return [...jobsOnDate, ...formattedOrders];
    };

    const tileContent = ({ date, view }: any) => {
        if (view === 'month') {
            const dateItems = getJobsForDate(date);
            if (dateItems.length > 0) {
                return (
                    <div className="text-center mt-1">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                            {dateItems.length}
                        </span>
                    </div>
                );
            }
        }
        return null;
    };

    const selectedDateJobs = getJobsForDate(selectedDate);
    const allItems = [...jobs, ...orders.map((order: any) => ({
        ...order,
        serviceType: `${order.certificateType} Certificate`,
        isOrder: true
    }))];

    const stats = {
        pending: allItems.filter(j => j.status === 'pending').length,
        dispatched: allItems.filter(j => j.status === 'dispatched').length,
        accepted: allItems.filter(j => j.status === 'accepted').length,
        completed: allItems.filter(j => j.status === 'completed').length,
    };

    return (
        <div className="flex-1 p-4 md:p-8 max-w-7xl mx-auto w-full">
            <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Partner Admin Dashboard</h2>
                <p className="text-gray-500 font-medium mt-1">Manage dispatch operations, monitor job statuses across the entire trade network.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-sm font-bold text-gray-500 uppercase">Pending</div>
                    <div className="text-3xl font-black text-gray-900">{stats.pending}</div>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-sm font-bold text-amber-500 uppercase">Dispatched</div>
                    <div className="text-3xl font-black text-amber-600">{stats.dispatched}</div>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-sm font-bold text-blue-500 uppercase">Accepted</div>
                    <div className="text-3xl font-black text-blue-600">{stats.accepted}</div>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="text-sm font-bold text-emerald-500 uppercase">Completed</div>
                    <div className="text-3xl font-black text-emerald-600">{stats.completed}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-4 bg-emerald-600 text-white font-bold h-14 flex items-center">
                            <i className="fas fa-calendar-alt mr-2 text-emerald-200"></i> Dispatch Calendar
                        </div>
                        <div className="p-4 custom-calendar-wrapper">
                            <Calendar
                                onChange={(val: any) => setSelectedDate(val)}
                                value={selectedDate}
                                tileContent={tileContent}
                                className="w-full border-0 !font-sans"
                            />
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden h-full">
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">
                                Jobs on {selectedDate.toLocaleDateString()}
                            </h3>
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold">
                                {selectedDateJobs.length} records
                            </span>
                        </div>
                        <div className="p-4 bg-gray-50 h-full min-h-[400px]">
                            {loading ? (
                                <div className="text-center py-10 text-gray-500">Loading jobs...</div>
                            ) : selectedDateJobs.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <i className="fas fa-calendar-times text-4xl mb-3 opacity-20"></i>
                                    <p>No jobs ordered for this date.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {selectedDateJobs.map(item => (
                                        <div key={item.id} className={`p-4 rounded-2xl shadow-sm border ${item.isOrder ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                                            <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-3">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        {item.isOrder && (
                                                            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-blue-600 text-white">
                                                                📋 Order
                                                            </span>
                                                        )}
                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${item.status === 'pending' ? 'bg-gray-100 text-gray-600' :
                                                                item.status === 'dispatched' ? 'bg-amber-100 text-amber-800' :
                                                                    item.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                                                                        item.status === 'accepted' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'
                                                            }`}>
                                                            {item.status}
                                                        </span>
                                                        <span className="text-xs text-gray-400 font-mono">{item.id.slice(0, 8)}</span>
                                                    </div>
                                                    <h4 className="font-bold text-gray-900">{item.serviceType}</h4>
                                                    <p className="text-sm font-medium text-gray-600 mt-1">{item.propertyAddress}</p>
                                                    <p className="text-sm text-gray-500">{item.tenantName} - {item.tenantPhone}</p>
                                                    {item.isOrder && item.accessDetails && (
                                                        <div className="mt-2 p-2 bg-white rounded border border-blue-100 text-xs">
                                                            <p className="font-bold text-blue-900">Engineer Contact: {item.accessDetails.contactName}</p>
                                                            <p className="text-blue-700">Phone: {item.accessDetails.contactPhone}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 text-xs font-bold">
                                                    {!item.isOrder && ['pending', 'declined'].includes(item.status) && (
                                                        <button
                                                            onClick={() => setSelectedJob(item)}
                                                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition"
                                                        >
                                                            DISPATCH
                                                        </button>
                                                    )}
                                                    {item.status !== 'cancelled' && item.status !== 'completed' && (
                                                        <button
                                                            onClick={() => handleCancel(item.id)}
                                                            className="bg-white border text-red-600 hover:bg-red-50 border-red-200 px-3 py-2 rounded-lg transition"
                                                        >
                                                            CANCEL
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {item.assignedPartnerName && (
                                                <div className="mt-3 bg-gray-50 px-3 py-2 rounded-xl text-xs flex justify-between items-center border border-gray-100">
                                                    <div>
                                                        <span className="text-gray-400 uppercase font-bold tracking-wider block mb-0.5">Assigned To</span>
                                                        <span className="font-bold text-gray-700">{item.assignedPartnerName}</span>
                                                    </div>
                                                    {item.status === 'dispatched' && (
                                                        <div className="text-amber-600 flex items-center gap-1">
                                                            <i className="fas fa-clock fa-spin"></i> Awaiting Accept
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dispatch Modal */}
            {selectedJob && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setSelectedJob(null)}></div>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md z-10 overflow-hidden">
                        <div className="bg-emerald-600 text-white px-6 py-4 flex justify-between items-center">
                            <h3 className="font-bold">Dispatch Job</h3>
                            <button onClick={() => setSelectedJob(null)} className="text-emerald-200 hover:text-white"><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleDispatch} className="p-6">
                            <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100 text-sm">
                                <div><strong>{selectedJob.serviceType}</strong></div>
                                <div className="text-gray-600">{selectedJob.propertyAddress}</div>
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Select Partner</label>
                                <select
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                                    value={dispatchPartnerId}
                                    onChange={(e) => setDispatchPartnerId(e.target.value)}
                                    required
                                >
                                    <option value="">-- Select a verified partner --</option>
                                    {partners.map(p => (
                                        <option key={p.id} value={p.id}>{p.businessName} ({p.tradeType}) - {p.email}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setSelectedJob(null)} className="flex-1 px-4 py-3 text-gray-600 font-bold bg-white border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
                                <button type="submit" disabled={isDispatching} className="flex-1 px-4 py-3 text-white font-bold bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50">
                                    {isDispatching ? 'Dispatching...' : 'Dispatch Now'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-calendar-wrapper .react-calendar__tile--active,
                .custom-calendar-wrapper .react-calendar__tile--active:enabled:hover,
                .custom-calendar-wrapper .react-calendar__tile--active:enabled:focus {
                    background: #059669;
                    color: white;
                    border-radius: 8px;
                }
                .custom-calendar-wrapper .react-calendar__tile--hasActive {
                    background: #10b981;
                }
                .custom-calendar-wrapper .react-calendar {
                    border: none;
                }
            `}} />
        </div>
    );
}
